/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import useOnClickOutside from "@/hooks/ui/useClickOutside";

import { SearchIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "search-autocomplete";

interface SearchAutocompleteProps<T> {
  options: T[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isSearchApi?: boolean;
  noDataTxt?: string;
  noDataTxtClassName?: string;
  getOptionLabel: (option: T) => string;
  getKey: (option: T) => string | number;
  onSelect: (option: T | null) => void;
  renderOption?: (option: T, keyword: string) => JSX.Element;
  setSearchKeyword?: (value: string) => void;
  onSearch?: (clearSearchKeyword: () => void) => void;
  showSuggestionMenu?: boolean;
  showNoDataAsOption?: boolean;
  testId?: string;
  error?: boolean;
}

const SearchAutocomplete = <T,>({
  options,
  className,
  placeholder = "Search...",
  disabled = false,
  isSearchApi = false,
  noDataTxt,
  showSuggestionMenu = true,
  noDataTxtClassName,
  showNoDataAsOption = false,
  getOptionLabel,
  getKey,
  onSelect,
  renderOption,
  setSearchKeyword,
  onSearch,
  testId = TEST_ID_PREFIX,
  error
}: SearchAutocompleteProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<T[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useOnClickOutside(ref, () => onSearch?.(() => setSearchTerm("")), []);

  useEffect(() => {
    if (isSearchApi) return;
    const filterOptions = () => {
      if (!searchTerm) {
        setFilteredOptions([]);
        return;
      }

      const filtered = options.filter((option) =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    };

    filterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, options, getOptionLabel]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setSearchKeyword?.(event.target.value);
    setShowSuggestions(true); // Show suggestions as the user types
    onSelect(null); // Clear the selected value in the parent component
  };

  const handleSuggestionClick = (option: T) => {
    setSearchTerm("");
    setFilteredOptions([]); // Clear suggestions after selection
    setShowSuggestions(false);
    onSelect(option);
  };

  const handleBlur = () => {
    // If the input loses focus and no option is selected, you might want to reset the search term or handle it differently.
    // For example you can choose to keep the suggestions or clear them.
    setTimeout(() => setShowSuggestions(false), 200); // Small delay to allow click event to fire
  };

  const onFocusInput = () => {
    if (searchTerm?.trim()?.length && filteredOptions?.length)
      setShowSuggestions(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isSearchApi && onSearch && event.key === "Enter") {
      onSearch(() => setSearchTerm(""));
      return;
    }
    if (event.key === "Enter" && filteredOptions.length > 0) {
      handleSuggestionClick(filteredOptions[0]); // Select the first option on Enter
    }
    if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const noData = noDataTxt && !filteredOptions.length && searchTerm?.length > 0;

  useEffect(() => {
    if (!isSearchApi) return;
    setFilteredOptions(options);
  }, [isSearchApi, options]);

  return (
    <div className="w-full relative" ref={ref}>
      <div
        className={twMerge(
          "flex items-center gap-1 border-[1px] border-gainsboro rounded-[12px] px-3 h-[40px] focus-within:border-teal-500",
          disabled && "cursor-not-allowed",
          (error || noDataTxt) &&
            "!border-red-500 !focus-within:border-red-500",
          className
        )}
      >
        <SearchIcon className="w-[16px]" />
        <input
          type="text"
          value={searchTerm}
          onFocus={onFocusInput}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="focus:outline-none w-full bg-transparent disabled:cursor-not-allowed"
          disabled={disabled}
          data-testid={`${testId}-search-input`}
        />
      </div>

      {showSuggestionMenu && showSuggestions && filteredOptions.length > 0 && (
        <div className="overflow-hidden absolute top-[45px] border border-gainsboro rounded-[8px] w-full bg-white z-[99]">
          <ul className="flex flex-col max-h-[240px] overflow-y-auto custom-scrollbar gap-1">
            {filteredOptions.length > 0 &&
              filteredOptions.map((option) => (
                <li
                  key={getKey(option)}
                  className="flex items-center cursor-pointer hover:bg-teal-50 px-2"
                  onClick={() => handleSuggestionClick(option)}
                >
                  {renderOption
                    ? renderOption(option, searchTerm)
                    : getOptionLabel(option)}
                </li>
              ))}
          </ul>
        </div>
      )}
      {noData && showNoDataAsOption && showSuggestions && (
        <div className="overflow-hidden absolute top-[45px] border border-gainsboro rounded-[8px] w-full bg-white z-[99]">
          <div className="flex h-[36px] items-center px-2 text-txtHeading">
            {noDataTxt}
          </div>
        </div>
      )}
      {noData && !showNoDataAsOption && (
        <div className="px-2 mt-1">
          <span
            className={twMerge("text-[10px] text-cinnabar", noDataTxtClassName)}
          >
            {noDataTxt}
          </span>
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
